import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import RichTextEditor from "@/components/common/RichTextEditor";
import { X, Send, Timer, CheckCircle, XCircle, Loader2 } from "lucide-react";

/**
 * NegotiationSheet — iter79 Batch 2 (Modul M · M2)
 *
 * Bottom-Sheet: Buyer schlägt einen Preis vor. Wenn der Buyer schon eine offene
 * Verhandlung hat, wird stattdessen der aktuelle Stand + Buttons (Counter
 * annehmen/ablehnen) angezeigt.
 *
 * Props: listing (required, must be a FIXED_PRICE MP listing), onClose,
 *        onNavigateToCheckout (nach Accept aus Sheet direkt in Checkout).
 *
 * Backend-Endpoints:
 * - POST /marketplace/listings/{id}/negotiate
 * - POST /marketplace/negotiations/{id}/buyer-respond
 * - GET  /marketplace/negotiations/mine
 */
export default function NegotiationSheet({ listing, onClose, onNavigateToCheckout }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [existing, setExisting] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);
  const [price, setPrice] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user || user === false) return;
    (async () => {
      try {
        const { data } = await api.get("/marketplace/negotiations/mine");
        const match = (data.as_buyer || []).find(n => n.listing_id === listing.listing_id
          && ["SUBMITTED", "SELLER_COUNTERED", "SELLER_ACCEPTED", "BUYER_ACCEPTED_COUNTER"].includes(n.status));
        setExisting(match || null);
      } catch { /* ignore */ }
      finally { setLoadingExisting(false); }
    })();
  }, [user, listing.listing_id]);

  const listPrice = Number(listing.price || 0);
  const currency = listing.currency || "EUR";

  const submit = async () => {
    setError("");
    const val = parseFloat(String(price).replace(",", "."));
    if (!val || val <= 0) { setError(t("negotiation.enter_price")); return; }
    if (val >= listPrice) { setError(t("negotiation.price_below_list_error", { price: listPrice.toFixed(2), currency })); return; }
    setSubmitting(true);
    try {
      // HTML aus RichTextEditor auf reinen Text reduzieren (Backend limitiert 500 Zeichen)
      const plainMessage = (message || "")
        .replace(/<br\s*\/?>/gi, "\n")
        .replace(/<\/(p|div|li|h[1-6])>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/&nbsp;/g, " ")
        .replace(/&amp;/g, "&")
        .replace(/&lt;/g, "<")
        .replace(/&gt;/g, ">")
        .replace(/\n{3,}/g, "\n\n")
        .trim();
      const { data } = await api.post(`/marketplace/listings/${listing.listing_id}/negotiate`, {
        proposed_price: val,
        message: plainMessage || undefined,
      });
      setExisting(data);
    } catch (e) { setError(formatError(e) || t("negotiation.send_error")); }
    finally { setSubmitting(false); }
  };

  const respondCounter = async (decision) => {
    if (!existing) return;
    setError(""); setSubmitting(true);
    try {
      const { data } = await api.post(`/marketplace/negotiations/${existing.negotiation_id}/buyer-respond`, { decision });
      setExisting(data);
      if (decision === "ACCEPT") {
        // Direkt zum Checkout mit ausgehandeltem Preis
        setTimeout(() => {
          onClose?.();
          onNavigateToCheckout?.(data);
        }, 800);
      }
    } catch (e) { setError(formatError(e) || t("negotiation.generic_error")); }
    finally { setSubmitting(false); }
  };

  if (!user || user === false) {
    return (
      <div data-testid="negotiation-sheet" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
        <div className="bg-white w-full sm:max-w-md sm:rounded-lg" onClick={(e) => e.stopPropagation()}>
          <SheetHeader onClose={onClose} title={t("negotiation.propose_price")} />
          <div className="px-4 py-6 text-center">
            <p className="text-sm text-[#666]">{t("negotiation.login_hint")}</p>
            <button data-testid="negotiation-login-cta" onClick={() => navigate("/login")}
              className="mt-4 rounded-full h-10 px-6 bg-[#0A0A0A] text-white text-sm">{t("negotiation.login")}</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="negotiation-sheet" className="fixed inset-0 z-[70] bg-black/50 flex items-end sm:items-center justify-center" onClick={onClose}>
      <div className="bg-white w-full sm:max-w-md sm:rounded-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <SheetHeader onClose={onClose} title={t("negotiation.title")} />

        {loadingExisting ? (
          <div className="flex justify-center py-8">
            <Loader2 size={24} className="animate-spin text-[#999]" />
          </div>
        ) : existing ? (
          <ExistingNegotiationView
            neg={existing}
            currency={currency}
            onCounterRespond={respondCounter}
            submitting={submitting}
            error={error}
          />
        ) : (
          <NewProposalForm
            listPrice={listPrice}
            currency={currency}
            price={price}
            setPrice={setPrice}
            message={message}
            setMessage={setMessage}
            error={error}
            submitting={submitting}
            submit={submit}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function SheetHeader({ onClose, title }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5] sticky top-0 bg-white">
      <p className="text-sm font-semibold text-[#0A0A0A]">{title}</p>
      <button data-testid="negotiation-close" onClick={onClose} aria-label={t("negotiation.close_aria")}>
        <X size={18} className="text-[#666]" />
      </button>
    </div>
  );
}

function NewProposalForm({ listPrice, currency, price, setPrice, message, setMessage, error, submitting, submit, onClose }) {
  const { t } = useTranslation();
  return (
    <>
      <div className="px-4 py-4 space-y-3">
        <div className="p-3 bg-[#FAFAFA] border border-[#E5E5E5] rounded-2xl">
          <p className="text-xs text-[#666]">{t("negotiation.list_price")}</p>
          <p className="text-lg font-bold text-[#0A0A0A]">{listPrice.toFixed(2)} {currency}</p>
          <p className="text-[10px] text-[#999] mt-1">{t("negotiation.seller_timeout")}</p>
        </div>
        {error && <p data-testid="negotiation-error" className="text-xs text-red-500">{error}</p>}
        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">{t("negotiation.your_proposal", { currency })}</label>
          <input
            data-testid="negotiation-price-input"
            type="text"
            inputMode="decimal"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder={t("negotiation.example", { price: (listPrice * 0.85).toFixed(2) })}
            className="w-full h-11 px-3 mt-1 border border-[#E5E5E5] rounded-full text-lg text-center focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
            autoFocus
          />
        </div>
        <div>
          <label className="text-xs font-medium text-[#666] uppercase tracking-wider">
            {t("negotiation.wish_label")}
          </label>
          <div className="mt-1" data-testid="negotiation-message-input">
            <RichTextEditor
              value={message}
              onChange={(html) => setMessage(html)}
              placeholder={t("negotiation.wish_placeholder")}
              minHeight="100px"
              testId="negotiation-message-rt"
            />
          </div>
          <p className="text-[10px] text-[#999] mt-1">
            {(() => {
              const plain = (message || "").replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ");
              return t("negotiation.counter", { count: plain.length });
            })()}
          </p>
        </div>
      </div>
      <div className="flex gap-2 px-4 pb-4 pt-1 border-t border-[#E5E5E5] sticky bottom-0 bg-white">
        <button onClick={onClose} className="rounded-full h-11 px-4 border border-[#E5E5E5] text-sm text-[#666]">{t("negotiation.cancel")}</button>
        <button
          data-testid="negotiation-submit"
          onClick={submit}
          disabled={submitting || !price}
          className="flex-1 rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-30">
          {submitting ? <><Loader2 size={14} className="animate-spin" /> {t("negotiation.sending")}</> : <><Send size={14} /> {t("negotiation.send")}</>}
        </button>
      </div>
    </>
  );
}

function ExistingNegotiationView({ neg, currency, onCounterRespond, submitting, error }) {
  const { t, i18n } = useTranslation();
  const state = neg.status;
  const localeCode = i18n.language?.startsWith("de") ? "de-DE" : i18n.language?.startsWith("nl") ? "nl-NL" : "en-US";
  const formatDate = (iso) => iso ? new Date(iso).toLocaleString(localeCode, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

  return (
    <div data-testid="negotiation-existing" className="px-4 py-4 space-y-3">
      {/* Timeline */}
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 rounded-full bg-[#0A0A0A] mt-2 shrink-0" />
          <div className="flex-1">
            <p className="text-xs font-semibold text-[#0A0A0A]">{t("negotiation.your_proposal_headline", { price: Number(neg.proposed_price).toFixed(2), currency })}</p>
            {neg.buyer_message && <p className="text-xs text-[#666] mt-0.5">&bdquo;{neg.buyer_message}&ldquo;</p>}
            <p className="text-[10px] text-[#999] mt-0.5">{formatDate(neg.created_at)}</p>
          </div>
        </div>
        {state === "SELLER_COUNTERED" && (
          <div className="flex items-start gap-2">
            <div className="w-2 h-2 rounded-full bg-amber-500 mt-2 shrink-0" />
            <InfoCard variant="warning" className="flex-1">
              <p className="text-xs font-semibold text-amber-900">{t("negotiation.counter_offer_seller", { price: Number(neg.counter_price).toFixed(2), currency })}</p>
              {neg.seller_message && <p className="text-xs text-amber-800 mt-0.5">&bdquo;{neg.seller_message}&ldquo;</p>}
              <p className="text-[10px] text-amber-700 mt-0.5">{t("negotiation.expires_on", { date: formatDate(neg.expires_at) })}</p>
            </InfoCard>
          </div>
        )}
        {(state === "SELLER_ACCEPTED" || state === "BUYER_ACCEPTED_COUNTER") && (
          <div className="flex items-start gap-2">
            <CheckCircle size={16} className="text-green-600 mt-1 shrink-0" />
            <InfoCard variant="success" className="flex-1">
              <p className="text-xs font-semibold text-green-900">{t("negotiation.deal_final_price", { price: Number(neg.final_price).toFixed(2), currency })}</p>
              <p className="text-[10px] text-green-700 mt-0.5">{t("negotiation.checkout_now_hint")}</p>
            </InfoCard>
          </div>
        )}
        {state === "CLOSED_REJECTED" && (
          <div className="flex items-start gap-2">
            <XCircle size={16} className="text-red-500 mt-1 shrink-0" />
            <InfoCard variant="danger" className="flex-1">
              <p className="text-xs font-semibold text-red-900">{t("negotiation.seller_declined")}</p>
              {neg.seller_message && <p className="text-xs text-red-800 mt-0.5">&bdquo;{neg.seller_message}&ldquo;</p>}
            </InfoCard>
          </div>
        )}
        {state === "CLOSED_EXPIRED" && (
          <div className="flex items-start gap-2">
            <Timer size={16} className="text-[#999] mt-1 shrink-0" />
            <div className="flex-1 p-2 bg-[#F5F5F5] border border-[#E5E5E5] rounded-2xl">
              <p className="text-xs font-semibold text-[#666]">{t("negotiation.expired")}</p>
            </div>
          </div>
        )}
        {state === "CLOSED_DECLINED" && (
          <div className="flex items-start gap-2">
            <XCircle size={16} className="text-[#999] mt-1 shrink-0" />
            <div className="flex-1 p-2 bg-[#F5F5F5] border border-[#E5E5E5] rounded-2xl">
              <p className="text-xs font-semibold text-[#666]">{t("negotiation.counter_declined_by_you")}</p>
            </div>
          </div>
        )}
        {state === "SUBMITTED" && (
          <div className="flex items-start gap-2">
            <Timer size={16} className="text-amber-500 mt-1 shrink-0" />
            <InfoCard variant="warning" className="flex-1">
              <p className="text-xs font-semibold text-amber-900">{t("negotiation.waiting_seller")}</p>
              <p className="text-[10px] text-amber-700 mt-0.5">{t("negotiation.expires_on", { date: formatDate(neg.expires_at) })}</p>
            </InfoCard>
          </div>
        )}
      </div>

      {error && <p data-testid="negotiation-error" className="text-xs text-red-500">{error}</p>}

      {/* Actions */}
      {state === "SELLER_COUNTERED" && (
        <div className="flex gap-2 pt-2 border-t border-[#E5E5E5]">
          <button
            data-testid="negotiation-decline-counter"
            onClick={() => onCounterRespond("DECLINE")}
            disabled={submitting}
            className="flex-1 rounded-full h-10 border border-[#E5E5E5] text-sm text-[#666] disabled:opacity-50">
            {t("negotiation.decline")}
          </button>
          <button
            data-testid="negotiation-accept-counter"
            onClick={() => onCounterRespond("ACCEPT")}
            disabled={submitting}
            className="flex-1 rounded-full h-10 bg-[#0A0A0A] text-white text-sm font-medium disabled:opacity-50">
            {submitting ? <Loader2 size={14} className="animate-spin mx-auto" /> : t("negotiation.accept")}
          </button>
        </div>
      )}
    </div>
  );
}
